import { searchRates } from '../../api/elivaas.js';
import { cacheElivaasRates, getCachedElivaasProperty } from '../../utils/elivaasCache.js';
import { apiGet, apiPost, apiPut } from '../services/apiClient';

const qs = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((item) => search.append(key, item));
      return;
    }
    search.set(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

const stripHtml = (value) =>
  String(value || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const text = String(value).replace(/,/g, '').trim();
  const match = text.match(/-?\d+(?:\.\d+)?/);
  const parsed = Number(match ? match[0] : text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSlug = (value, fallback = '') =>
  String(value || fallback || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const humanizeRatePlanCode = (value) => {
  const code = String(value || '').trim().toLowerCase();
  if (!code) return 'Property only';
  const lookup = {
    ep: 'Property only',
    'cp-inc-groceries': 'Breakfast only',
    'one-meal-inc-groceries': 'One meal included',
    'map-inc-groceries': 'Breakfast + dinner',
    'two-meals-lunch-dinner-inc-groceries': 'Lunch + dinner',
    'cp-chef-on-call': 'Breakfast with chef',
    'one-meal-only-chef-on-call': 'One meal with chef',
    'ap-inc-groceries': 'All meals included',
    'map-chef-on-call': 'Breakfast + dinner with chef',
    'two-meals-lunch-dinner-chef-on-call': 'Lunch + dinner with chef',
    'ap-chef-on-call': 'All meals with chef',
  };
  if (lookup[code]) return lookup[code];
  return String(code)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const pickProviderQuote = (quotes = [], { quoteId, ratePlanCode } = {}) => {
  const items = Array.isArray(quotes) ? quotes.filter(Boolean) : [];
  if (!items.length) return null;
  const normalizedQuoteId = String(quoteId || '').trim();
  const normalizedRatePlanCode = String(ratePlanCode || '').trim();
  if (normalizedQuoteId) {
    const matchByQuoteId = items.find((quote) => String(quote?.quoteId || '').trim() === normalizedQuoteId);
    if (matchByQuoteId) return matchByQuoteId;
  }
  if (normalizedRatePlanCode) {
    const matchByRatePlan = items.find((quote) => String(quote?.ratePlanCode || '').trim() === normalizedRatePlanCode);
    if (matchByRatePlan) return matchByRatePlan;
  }
  return items[0] || null;
};

const humanizeToken = (value, fallback = '') =>
  String(value || fallback || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const extractImageUrl = (image) => {
  if (!image) return '';
  if (typeof image === 'string') return image.trim();
  if (typeof image !== 'object') return '';
  return String(
    image.image_url ||
      image.url ||
      image.src ||
      image.image ||
      image.imageUrl ||
      image.original ||
      image.originalImageUrl ||
      image.original_image_url ||
      image.originalUrl ||
      image.original_url ||
      image.secure_url ||
      image.webUrl ||
      image.web_url ||
      image.desktopUrl ||
      image.desktop_url ||
      image.cdnUrl ||
      image.cdn_url ||
      image.signedUrl ||
      image.signed_url ||
      image.sourceUrl ||
      image.source_url ||
      image.fileUrl ||
      image.file_url ||
      image.mobileUrl ||
      image.mobile_url ||
      image.thumbnailUrl ||
      image.thumbnail_url ||
      image.largeUrl ||
      image.large_url ||
      image.featured_image ||
      image.cover_image ||
      image.coverImage ||
      image.file?.url ||
      image.asset?.url ||
      image.defaultMedia?.url ||
      image.defaultMedia?.mobileUrl ||
      image.image?.url ||
      image.image?.mobileUrl ||
      ''
  ).trim();
};

const collectImageCandidates = (input, depth = 0, seen = new WeakSet()) => {
  if (!input) return [];
  if (Array.isArray(input)) return input.flatMap((item) => collectImageCandidates(item, depth + 1, seen));
  if (typeof input !== 'object') return [input];
  if (depth > 4 || seen.has(input)) return [];
  seen.add(input);

  const nestedCollections = [
    input.images,
    input.gallery,
    input.galleryImages,
    input.photos,
    input.media,
    input.medias,
    input.propertyImages,
    input.property_images,
    input.assets,
    input.items,
    input.files,
    input.urls,
    input.rooms,
    input.roomTypes,
    input.spaces,
  ];

  return [
    input,
    input.featured_image,
    input.cover_image,
    input.imageUrl,
    input.image_url,
    input.image,
    input.defaultMedia,
    ...nestedCollections.flatMap((value) => collectImageCandidates(value, depth + 1, seen)),
  ].filter(Boolean);
};

const toImageObjects = (images = [], title = 'Homevilas stay') => {
  const seen = new Set();
  return collectImageCandidates(images)
    .map((image, index) => {
      const url = extractImageUrl(image);
      if (!url || seen.has(url)) return null;
      seen.add(url);
      if (typeof image === 'string') {
        return {
          id: `image-${index + 1}`,
          image_url: url,
          alt_text: title,
          sort_order: index + 1,
          is_cover: index === 0,
        };
      }
      return {
        ...image,
        id: image.id || `image-${index + 1}`,
        image_url: url,
        alt_text: image.alt_text || image.alt || image.title || title,
        sort_order: image.sort_order || index + 1,
        is_cover: image.is_cover ?? index === 0,
      };
    })
    .filter(Boolean);
};

const collectAmenityCandidates = (value, depth = 0, seen = new WeakSet()) => {
  if (!value || depth > 4) return [];
  if (Array.isArray(value)) return value.flatMap((item) => collectAmenityCandidates(item, depth + 1, seen));
  if (typeof value === 'string') return [value];
  if (typeof value !== 'object') return [];
  if (seen.has(value)) return [];
  seen.add(value);
  const nestedCollections = [
    value.items,
    value.list,
    value.data,
    value.options,
    value.values,
    value.children,
    value.amenities,
    value.amenityList,
    value.amenity_list,
    value.facilities,
    value.facilityList,
    value.facility_list,
    value.features,
    value.inclusions,
  ];
  return [
    value,
    ...nestedCollections.flatMap((item) => collectAmenityCandidates(item, depth + 1, seen)),
  ].filter(Boolean);
};

const toAmenityItems = (amenities = []) =>
  collectAmenityCandidates(amenities)
    .map((amenity, index) => {
      if (!amenity) return null;
      if (typeof amenity === 'string') {
        const name = amenity.trim();
        if (!name) return null;
        return {
          id: `amenity-${index + 1}`,
          name,
          slug: toSlug(name, `amenity-${index + 1}`),
          category: 'Amenity',
          description: '',
          icon: '',
          is_top_feature: false,
        };
      }
      if (typeof amenity === 'object') {
        const name =
          amenity.name ||
          amenity.label ||
          amenity.title ||
          amenity.slug ||
          amenity.amenityName ||
          amenity.amenity_name ||
          amenity.facilityName ||
          amenity.facility_name ||
          amenity.displayName ||
          amenity.display_name ||
          amenity.text ||
          amenity.value;
        if (!name) return null;
        return {
          id: amenity.id || `amenity-${index + 1}`,
          name,
          slug: amenity.slug || toSlug(name, `amenity-${index + 1}`),
          category: amenity.category || amenity.group || amenity.type || 'Amenity',
          description: amenity.description || '',
          icon: amenity.icon || amenity.icon_url || amenity.image_url || '',
          is_top_feature: Boolean(
            amenity.is_top_feature || amenity.isTopFeature || amenity.is_highlight || amenity.isHighlight
          ),
        };
      }
      return null;
    })
    .filter(Boolean);

const mergeAmenityItems = (...groups) => {
  const seen = new Set();
  return groups
    .flatMap((group) => (Array.isArray(group) ? group : []))
    .filter(Boolean)
    .filter((amenity) => {
      const key = String(amenity.slug || amenity.name || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const buildProviderFallbackAmenities = (raw = {}, propertyRow = {}, snapshot = {}) => {
  const text = [
    raw?.name,
    raw?.displayName,
    raw?.osDisplayName,
    raw?.description,
    raw?.propertyGroupName,
    propertyRow?.name,
    propertyRow?.title,
    propertyRow?.propertyName,
    snapshot?.name,
    snapshot?.title,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const candidates = [
    { key: 'private-balcony', name: 'Private balcony', match: /\b(private\s+)?balcon(y|ies)\b/ },
    { key: 'bathtub', name: 'Bathtub', match: /\bbathtub\b|\bbath\s*tub\b/ },
    { key: 'pet-friendly', name: 'Pet friendly', match: /\bpet[-\s]?friendly\b|\bpets?\b/ },
    { key: 'restaurant', name: 'Restaurant access', match: /\brestro\b|\brestaurant\b|\bdining\b/ },
    { key: 'hillside-views', name: 'Hillside views', match: /\bhillside\b|\bvalley\b|\bmountain\b|\bview(s)?\b/ },
    { key: 'golf-course-nearby', name: 'Near golf course', match: /\bgolf\s+course\b/ },
    { key: 'wi-fi', name: 'Wi-Fi', match: /\bwi[-\s]?fi\b|\binternet\b/ },
    { key: 'virtual-tour', name: 'Virtual tour', match: /\bvirtual\s+tour\b/ },
  ];

  const inferred = candidates
    .filter((item) => item.match.test(text) || (item.key === 'virtual-tour' && raw?.virtualTourUrl))
    .map((item, index) => ({
      id: `provider-fallback-amenity-${index + 1}`,
      name: item.name,
      slug: item.key,
      category: 'Amenity',
      description: '',
      icon: '',
      is_top_feature: index < 4,
    }));

  const metricAmenities = (Array.isArray(raw?.metrics) ? raw.metrics : [])
    .map((metric, index) => {
      const label = String(metric?.name || '').trim();
      const value = String(metric?.value || '').trim();
      if (!label || !value || Number(value) <= 0) return null;
      return {
        id: `provider-metric-amenity-${index + 1}`,
        name: `${value} ${label}`,
        slug: toSlug(`${value} ${label}`, `provider-metric-amenity-${index + 1}`),
        category: 'Stay details',
        description: '',
        icon: '',
        is_top_feature: inferred.length < 4 && index < 2,
      };
    })
    .filter(Boolean);

  return mergeAmenityItems(inferred, metricAmenities);
};

const normalizeAddonItems = (addons = []) =>
  (Array.isArray(addons) ? addons : [])
    .map((addon, index) => {
      if (!addon) return null;
      if (typeof addon === 'string') {
        const title = addon.trim();
        if (!title) return null;
        return {
          id: `addon-${index + 1}`,
          title,
          short_description: '',
          image_url: '',
          amount: 0,
          pricing_type: 'per_booking',
          max_quantity: 1,
          is_active: true,
          is_selectable: false,
          availability_note: 'Available on request',
          badge_label: '',
          collection_label: '',
          source_type: 'derived',
          sort_order: index,
        };
      }
      if (typeof addon === 'object') {
        const title = String(addon.title || addon.name || addon.label || '').trim();
        if (!title) return null;
        const amount = Number(addon.amount ?? addon.price ?? addon.value ?? 0) || 0;
        const selectable =
          addon.is_selectable !== undefined
            ? Boolean(addon.is_selectable)
            : amount > 0;
        return {
          id: addon.id || `addon-${index + 1}`,
          title,
          short_description: String(
            addon.short_description || addon.subtitle || addon.description || ''
          ).trim(),
          image_url: String(addon.image_url || addon.image || addon.url || '').trim(),
          amount,
          pricing_type: addon.pricing_type || addon.price_type || 'per_booking',
          max_quantity: Number(addon.max_quantity ?? 1) || 1,
          is_active: addon.is_active !== false,
          is_selectable: selectable,
          availability_note: String(addon.availability_note || addon.note || '').trim(),
          badge_label: String(addon.badge_label || '').trim(),
          collection_label: String(addon.collection_label || '').trim(),
          source_type: String(addon.source_type || 'admin').trim(),
          sort_order: Number(addon.sort_order ?? index) || index,
        };
      }
      return null;
    })
    .filter(Boolean);

const parseSections = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const buildAboutHomeTabs = (property, cancellationPolicy) => {
  const houseRulesSections = parseSections(property.house_rules_sections);
  const tabs = [
    {
      id: 'checkin',
      label: 'Check-in',
      content: {
        text: [
          property.check_in_time ? `Check-in from ${property.check_in_time}.` : '',
          property.check_out_time ? `Check-out by ${property.check_out_time}.` : '',
          property.min_nights ? `Minimum stay: ${property.min_nights} night${property.min_nights > 1 ? 's' : ''}.` : '',
        ]
          .filter(Boolean)
          .join(' '),
      },
    },
  ];

  if (houseRulesSections.length) {
    tabs.push({
      id: 'house-rules',
      label: 'House Rules',
      content: { sections: houseRulesSections },
    });
  } else if (property.description) {
    tabs.push({
      id: 'house-rules',
      label: 'About Stay',
      content: { text: property.description },
    });
  }

  if (cancellationPolicy.length) {
    tabs.push({
      id: 'cancellation',
      label: 'Cancellation',
      content: {
        list: cancellationPolicy.map((rule) => `${rule.label}: ${rule.refund_percent}% refund${rule.description ? ` - ${rule.description}` : ''}`),
      },
    });
  }

  if (property.security_deposit_policy) {
    tabs.push({
      id: 'security',
      label: 'Security',
      content: { text: property.security_deposit_policy },
    });
  }

  return tabs;
};

const buildFeatureBadges = (property) => {
  const badges = [];
  const tags = Array.isArray(property.tags) ? property.tags.slice(0, 3) : [];
  const provider = property.badges?.provider || property.provider || '';
  const propertyText = [
    property.title,
    property.name,
    property.pet_policy,
    property.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (provider) {
    badges.push({
      id: 'badge-provider',
      badge_type: 'Provider',
      badge_title: String(provider).toUpperCase(),
      badge_subtitle: 'Managed via channel sync',
    });
  }

  if (/(pet-friendly|pets are allowed|pet friendly)/i.test(propertyText)) {
    badges.unshift({
      id: 'badge-pet-friendly',
      badge_type: 'Stay highlight',
      badge_title: 'Pet Friendly',
      badge_subtitle: 'Pets are welcome at this stay',
    });
  }

  tags.forEach((tag, index) => {
    badges.push({
      id: `badge-tag-${index + 1}`,
      badge_type: 'Highlight',
      badge_title: String(tag),
      badge_subtitle: 'Property feature',
    });
  });

  return badges;
};

const buildCancellationPolicy = (property) => {
  const text =
    property.cancellation_policy ||
    property.cancellationPolicy ||
    property.policy?.cancellation_policy ||
    '';
  if (!text) return [];
  return [
    {
      id: 'policy-1',
      rule_type: 'custom',
      label: 'Cancellation terms',
      description: stripHtml(text),
      from_days: null,
      to_days: null,
      refund_percent: 0,
    },
  ];
};

const normalizeRoomTypes = (roomTypes = [], property) =>
  (Array.isArray(roomTypes) ? roomTypes : [])
    .map((room, index) => {
      if (!room || typeof room !== 'object') return null;
      return {
        ...room,
        id: room.id || `room-${index + 1}`,
        name: room.name || room.title || `Room ${index + 1}`,
        currency: room.currency || property.currency || 'INR',
      };
    })
    .filter(Boolean);

const extractProviderMetricNumber = (metrics = [], metricName = '') => {
  const entry = (Array.isArray(metrics) ? metrics : []).find((item) =>
    String(item?.name || '').toLowerCase().includes(String(metricName || '').toLowerCase())
  );
  if (!entry) return 0;
  const directValue = Number(entry?.value ?? entry?.count ?? entry?.total ?? 0);
  if (Number.isFinite(directValue) && directValue > 0) return directValue;
  const text = String(entry?.value || entry?.label || entry?.description || '').trim();
  const match = text.match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const findProviderSection = (raw, slug) =>
  (Array.isArray(raw?.sections) ? raw.sections : []).find(
    (section) => String(section?.slug || '').trim().toUpperCase() === String(slug || '').trim().toUpperCase()
  ) || null;

const parseProviderSectionLines = (value) =>
  stripHtml(value)
    .split(/\s*\|\s*|\s{2,}|\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 20);

const extractProviderTime = (value, label) => {
  const text = stripHtml(value);
  const match = text.match(new RegExp(`${label}\\s*:?\\s*([^|.]+)`, 'i'));
  return match ? match[1].trim() : '';
};

const getHtmlDocument = (value) => {
  const markup = String(value || '').trim();
  if (!markup || typeof DOMParser === 'undefined') return null;
  try {
    return new DOMParser().parseFromString(markup, 'text/html');
  } catch {
    return null;
  }
};

const parseProviderHtmlContent = (value) => {
  const text = stripHtml(value);
  if (!text) {
    return { text: '', paragraphs: [], list: [], sections: [] };
  }

  const doc = getHtmlDocument(value);
  if (!doc?.body) {
    return {
      text,
      paragraphs: text ? text.split(/\.\s+/).map((item) => item.trim()).filter(Boolean) : [],
      list: parseProviderSectionLines(text),
      sections: [],
    };
  }

  const paragraphs = [];
  const list = [];
  const sections = [];
  let activeSection = null;

  const flushActiveSection = () => {
    if (!activeSection) return;
    const items = Array.isArray(activeSection.items) ? activeSection.items.filter(Boolean) : [];
    if (activeSection.title && items.length) {
      sections.push({
        title: activeSection.title,
        items,
      });
    } else if (items.length) {
      list.push(...items);
    }
    activeSection = null;
  };

  Array.from(doc.body.children || []).forEach((node) => {
    const tag = String(node.tagName || '').toLowerCase();
    const nodeText = stripHtml(node.innerHTML || node.textContent || '');
    if (!nodeText) return;

    if (/^h[1-6]$/.test(tag)) {
      flushActiveSection();
      activeSection = { title: nodeText, items: [] };
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      const items = Array.from(node.querySelectorAll('li'))
        .map((item) => stripHtml(item.innerHTML || item.textContent || ''))
        .filter(Boolean);
      if (activeSection) activeSection.items.push(...items);
      else list.push(...items);
      return;
    }

    if (tag === 'p') {
      const strong = node.querySelector('strong');
      const strongText = stripHtml(strong?.innerHTML || strong?.textContent || '');
      if (strongText && strongText.length <= 80 && strongText !== nodeText) {
        flushActiveSection();
        activeSection = {
          title: strongText.replace(/:$/, ''),
          items: [nodeText.replace(new RegExp(`^${escapeRegExp(strongText)}\\s*:?\\s*`, 'i'), '').trim()].filter(Boolean),
        };
        return;
      }
      if (activeSection) activeSection.items.push(nodeText);
      else paragraphs.push(nodeText);
      return;
    }

    if (activeSection) activeSection.items.push(nodeText);
    else paragraphs.push(nodeText);
  });

  flushActiveSection();

  return {
    text: paragraphs.join(' '),
    paragraphs,
    list: uniqueProviderItems(list),
    sections: sections
      .map((section) => ({
        ...section,
        items: uniqueProviderItems(section.items),
      }))
      .filter((section) => section.title || section.items.length),
  };
};

const uniqueProviderItems = (items = []) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const parseProviderFaqs = (faqGroups = []) =>
  (Array.isArray(faqGroups) ? faqGroups : [])
    .flatMap((group) =>
      (Array.isArray(group?.list) ? group.list : []).map((faq, index) => ({
        id: `${String(group?.category || 'faq').toLowerCase()}-${index + 1}`,
        question: String(faq?.question || '').trim(),
        answer: String(faq?.answer || '').trim(),
        category: String(group?.category || '').trim(),
      }))
    )
    .filter((faq) => faq.question || faq.answer);

const parseNearbyPointsFromSection = (section) => {
  const doc = getHtmlDocument(section?.content || '');
  if (doc?.body) {
    return Array.from(doc.querySelectorAll('li'))
      .map((item, index) => {
        const text = stripHtml(item.innerHTML || item.textContent || '');
        if (!text) return null;
        const match = text.match(/(.+?)\s+[–-]\s+([\d.]+)\s*km/i);
        return {
          id: `provider-nearby-${index + 1}`,
          title: match ? match[1].trim() : text,
          distance_km: match ? Number(match[2]) : null,
          category: 'Nearby',
        };
      })
      .filter(Boolean);
  }

  return parseProviderSectionLines(section?.content)
    .map((line, index) => {
      const match = line.match(/(.+?)\s+[–-]\s+([\d.]+)\s*km/i);
      return {
        id: `provider-nearby-${index + 1}`,
        title: match ? match[1].trim() : line,
        distance_km: match ? Number(match[2]) : null,
        category: 'Nearby',
      };
    })
    .filter(Boolean);
};

const buildProviderMealPlans = (quotes = [], defaultQuote = null, fallbackDescription = '') =>
  (Array.isArray(quotes) ? quotes : [])
    .map((quote, index) => {
      const ratePlanCode = String(quote?.ratePlanCode || '').trim();
      if (!ratePlanCode) return null;
      return {
        id: ratePlanCode,
        property_id: String(quote?.propertyId || ''),
        title: humanizeRatePlanCode(ratePlanCode),
        description:
          String(quote?.ratePlanName || '').trim() ||
          fallbackDescription ||
          'Provider meal/package plan',
        price_type: 'per_booking',
        amount: Number(quote?.mealCost || 0) || 0,
        is_default:
          String(quote?.quoteId || '') === String(defaultQuote?.quoteId || '') ||
          String(ratePlanCode) === String(defaultQuote?.ratePlanCode || ''),
        sort_order: index,
        rate_plan_code: ratePlanCode,
        quote_id: String(quote?.quoteId || ''),
        total_price: Number(quote?.netAmountAfterTax || 0) || 0,
        currency: quote?.currency || 'INR',
      };
    })
    .filter(Boolean);

const resolveProviderMediaUrl = (value) =>
  String(
    value?.image_url ||
      value?.image ||
      value?.url ||
      value?.src ||
      value?.mobileUrl ||
      value?.defaultMedia?.url ||
      value ||
      ''
  ).trim();

const findProviderVisual = ({ spaces = [], images = [], raw = null, keywords = [] }) => {
  const terms = (Array.isArray(keywords) ? keywords : [])
    .map((keyword) => String(keyword || '').trim().toLowerCase())
    .filter(Boolean);
  const spaceMatch = (Array.isArray(spaces) ? spaces : []).find((space) => {
    const text = `${space?.title || ''} ${space?.subtitle || ''}`.toLowerCase();
    return terms.some((term) => text.includes(term));
  });
  if (spaceMatch?.image_url) return spaceMatch.image_url;

  const imageMatch = (Array.isArray(images) ? images : []).find((image) => {
    const text = `${image?.title || ''} ${image?.description || ''} ${image?.alt_text || ''}`.toLowerCase();
    return terms.some((term) => text.includes(term));
  });
  if (imageMatch) return resolveProviderMediaUrl(imageMatch);

  return resolveProviderMediaUrl(raw?.defaultMedia) || resolveProviderMediaUrl(images?.[0]) || '';
};

const buildProviderFallbackAddons = ({
  raw = null,
  aboutText = '',
  houseRulesText = '',
  mealsSection = null,
  spaces = [],
  images = [],
}) => {
  const cards = [];
  const sectionLabel = raw?.isPrive ? 'VIP Indulgences' : 'Curated upgrades';
  const pushCard = (card) => {
    if (!card?.title) return;
    const key = String(card.title).trim().toLowerCase();
    if (!key || cards.some((item) => String(item.title).trim().toLowerCase() === key)) return;
    cards.push({
      ...card,
      collection_label: sectionLabel,
      source_type: 'provider_hint',
      is_selectable: false,
      availability_note: card.availability_note || 'Available on request',
      amount: 0,
    });
  };

  const providerText = `${aboutText} ${houseRulesText} ${mealsSection?.content || ''}`;

  if (/bonfire/i.test(providerText)) {
    pushCard({
      id: 'provider-addon-bonfire',
      title: 'Bonfire Setup',
      short_description: 'Bonfire setup is available on request for evening gatherings.',
      image_url: findProviderVisual({ spaces, images, raw, keywords: ['bonfire', 'gazebo', 'pool'] }),
      badge_label: 'On request',
    });
  }

  if (/chef on call/i.test(mealsSection?.content || '')) {
    pushCard({
      id: 'provider-addon-chef',
      title: 'Chef on Call',
      short_description: 'In-house menu and chef-on-call service can be arranged during your stay.',
      image_url: resolveProviderMediaUrl(mealsSection?.url) || findProviderVisual({ spaces, images, raw, keywords: ['dining', 'meals', 'kitchen'] }),
      badge_label: 'Dining',
    });
  }

  if (/(tea and coffee|tea & coffee|high tea)/i.test(providerText)) {
    pushCard({
      id: 'provider-addon-tea',
      title: 'Tea & Coffee Service',
      short_description: 'Tea and coffee setup is available as part of the elevated in-room experience.',
      image_url: findProviderVisual({ spaces, images, raw, keywords: ['bedroom', 'lounge', 'dining'] }),
      badge_label: 'Stay upgrade',
    });
  }

  if (/(pets are allowed|pet-friendly|pet friendly)/i.test(providerText)) {
    pushCard({
      id: 'provider-addon-pet',
      title: 'Pet Stay Support',
      short_description: 'Pet-friendly stay support is available subject to deposit and sanitation charges.',
      image_url: findProviderVisual({ spaces, images, raw, keywords: ['garden', 'lawn', 'outdoor'] }),
      badge_label: 'Pet friendly',
    });
  }

  return normalizeAddonItems(cards).slice(0, 4);
};

const buildProviderOfferCards = (quote = {}, quotes = []) => {
  const source = Array.isArray(quote?.provider_offer_cards) && quote.provider_offer_cards.length
    ? quote.provider_offer_cards
    : (Array.isArray(quotes) ? quotes : [])
        .flatMap((item) => {
          const cards = [];
          if (item?.promotionCode && Number(item?.promotionDiscountAmount || 0) > 0) {
            cards.push({
              id: `provider-promo:${String(item.promotionCode).toUpperCase()}`,
              title: String(item.promotionCode).toUpperCase(),
              subtitle: humanizeRatePlanCode(item?.ratePlanCode),
              description: 'Provider-managed savings already reflected in the displayed rate.',
              offer_type: 'provider_promotion',
              offer_origin: 'provider',
              is_auto_applied: true,
              discount_type: 'flat',
              discount_value: Number(item?.promotionDiscountAmount || 0),
              action_type: 'none',
              action_value: '',
            });
          }
          return cards;
        });

  const seen = new Set();
  return source.filter((offer) => {
    const key = String(offer?.id || offer?.code || offer?.title || '').trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildProviderFeatureBadges = (propertyRow, snapshot, houseRulesText = '') => {
  const badges = [];
  const providerText = [
    propertyRow?.name,
    propertyRow?.propertyName,
    snapshot?.title,
    snapshot?.name,
    houseRulesText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(pet-friendly|pet friendly|pets are allowed)/i.test(providerText)) {
    badges.push({
      id: 'badge-provider-pet-friendly',
      badge_type: 'Stay highlight',
      badge_title: 'Pet Friendly',
      badge_subtitle: 'Pets are welcome at this stay',
    });
  }

  if (snapshot?.isPrive || propertyRow?.isPrive) {
    badges.push({
      id: 'badge-provider-prive',
      badge_type: 'Collection',
      badge_title: 'Prive Collection',
      badge_subtitle: 'Signature stay with elevated experiences',
    });
  }

  if (propertyRow?.highDemand) {
    badges.push({
      id: 'badge-provider-high-demand',
      badge_type: 'Highlight',
      badge_title: 'High Demand',
      badge_subtitle: 'Frequently booked stay',
    });
  }

  if (propertyRow?.new) {
    badges.push({
      id: 'badge-provider-new',
      badge_type: 'Highlight',
      badge_title: 'New Arrival',
      badge_subtitle: 'Recently added stay',
    });
  }

  badges.push({
    id: 'badge-provider-elivaas',
    badge_type: 'Provider',
    badge_title: 'ELIVAAS',
    badge_subtitle: 'Channel-managed stay',
  });

  return badges.slice(0, 4);
};

const buildProviderReviewSources = (propertyRow) =>
  Number(propertyRow?.tripAdvisorLocationId || 0) || readProviderRating(propertyRow)
    ? [
        {
          id: Number(propertyRow?.tripAdvisorLocationId || 0) ? 'tripadvisor' : 'elivaas',
          source_name: Number(propertyRow?.tripAdvisorLocationId || 0) ? 'Tripadvisor' : 'ELIVAAS',
          source_rating: readProviderRating(propertyRow),
          source_review_count: readProviderReviewCount(propertyRow),
          summary_text: '',
        },
      ]
    : [];

const normalizeProviderReviews = (...sources) => {
  const rows = sources.flatMap((source) => {
    if (!source) return [];
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.reviews)) return source.reviews;
    if (Array.isArray(source?.reviewList)) return source.reviewList;
    if (Array.isArray(source?.review_list)) return source.review_list;
    if (Array.isArray(source?.reviews?.items)) return source.reviews.items;
    if (Array.isArray(source?.reviews?.list)) return source.reviews.list;
    if (Array.isArray(source?.reviewsSummary?.items)) return source.reviewsSummary.items;
    if (Array.isArray(source?.reviews_summary?.items)) return source.reviews_summary.items;
    return [];
  });
  const seen = new Set();
  return rows
    .map((review, index) => {
      if (!review) return null;
      if (typeof review === 'string') {
        const comment = stripHtml(review);
        if (!comment) return null;
        return {
          id: `provider-review-${index + 1}`,
          rating: 0,
          guest_name: '',
          comment,
          created_at: null,
        };
      }
      const rating = toNumber(
        review.rating ?? review.reviewRating ?? review.review_rating ?? review.score ?? review.stars
      );
      const comment = stripHtml(
        review.comment || review.review || review.reviewText || review.review_text || review.text || review.description
      );
      const guestName = String(
        review.guest_name ||
          review.guestName ||
          review.reviewer_name ||
          review.reviewerName ||
          review.name ||
          review.author ||
          ''
      ).trim();
      if (!rating && !comment && !guestName) return null;
      const id = String(review.id || review.reviewId || review.review_id || `${guestName}:${comment}:${index}`).trim();
      if (seen.has(id)) return null;
      seen.add(id);
      return {
        ...review,
        id,
        rating,
        guest_name: guestName,
        comment,
        created_at: review.created_at || review.createdAt || review.date || review.reviewDate || review.review_date || null,
      };
    })
    .filter(Boolean);
};

const firstPositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

const firstCountNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return 0;
};

const readProviderRating = (...sources) =>
  firstPositiveNumber(
    ...sources.flatMap((source) => [
      source?.avg_rating,
      source?.average_rating,
      source?.rating,
      source?.avgRating,
      source?.averageRating,
      source?.starRating,
      source?.star_rating,
      source?.reviewRating,
      source?.review_rating,
      source?.ratingSummary?.rating,
      source?.ratingSummary?.avg,
      source?.rating_summary?.rating,
      source?.rating_summary?.avg,
      source?.reviewsSummary?.rating,
      source?.reviewsSummary?.avg,
      source?.reviewsSummary?.average,
      source?.reviewsSummary?.averageRating,
      source?.reviews_summary?.rating,
      source?.reviews_summary?.avg,
      source?.reviews_summary?.average,
      source?.reviews_summary?.average_rating,
      source?.reviewStats?.rating,
      source?.reviewStats?.avg,
      source?.review_stats?.rating,
      source?.review_stats?.avg,
      source?.reviews?.rating,
      source?.reviews?.avg,
      source?.reviews?.average,
      source?.reviews?.averageRating,
      source?.tripAdvisorRating,
      source?.tripadvisor_rating,
      source?.googleRating,
      source?.google_rating,
    ])
  );

const readProviderReviewCount = (...sources) =>
  firstCountNumber(
    ...sources.flatMap((source) => [
      source?.review_count,
      source?.reviews_count,
      source?.reviewCount,
      source?.reviewsCount,
      source?.totalReviews,
      source?.total_reviews,
      source?.ratingCount,
      source?.rating_count,
      source?.ratingSummary?.count,
      source?.ratingSummary?.reviewCount,
      source?.rating_summary?.count,
      source?.rating_summary?.review_count,
      source?.reviewsSummary?.count,
      source?.reviewsSummary?.reviewCount,
      source?.reviewsSummary?.total,
      source?.reviews_summary?.count,
      source?.reviews_summary?.review_count,
      source?.reviews_summary?.total,
      source?.reviewStats?.count,
      source?.reviewStats?.reviewCount,
      source?.review_stats?.count,
      source?.review_stats?.review_count,
      source?.reviews?.count,
      source?.reviews?.total,
      source?.reviews?.reviewCount,
      Array.isArray(source?.reviews) ? source.reviews.length : null,
      Array.isArray(source?.reviewList) ? source.reviewList.length : null,
      Array.isArray(source?.review_list) ? source.review_list.length : null,
    ])
  );

const buildProviderExtraSections = (raw = {}, knownSlugs = []) =>
  (Array.isArray(raw?.sections) ? raw.sections : [])
    .filter((section) => !knownSlugs.includes(String(section?.slug || '').trim().toUpperCase()))
    .map((section, index) => {
      const parsed = parseProviderHtmlContent(section?.content);
      if (!parsed.text && !parsed.list.length && !parsed.sections.length) return null;
      const title = String(section?.title || humanizeToken(section?.slug || `section-${index + 1}`)).trim();
      return {
        id: `provider-extra-${index + 1}`,
        anchor_id: `provider-extra-${index + 1}`,
        label: title,
        title,
        content: {
          text: parsed.text,
          list: parsed.list,
          sections: parsed.sections,
        },
      };
    })
    .filter(Boolean);

const extractProviderMinNights = (value) => {
  const text = stripHtml(value);
  const match = text.match(/minimum(?:\s+stay)?\s*:?[\s-]*(\d+)/i);
  return match ? Number(match[1]) : 0;
};

const buildProviderCancellationFallback = (houseRulesSection = null) => {
  const parsed = parseProviderHtmlContent(houseRulesSection?.content || '');
  const lines = uniqueProviderItems([
    ...parsed.paragraphs,
    ...parsed.list,
    ...parsed.sections.flatMap((section) => section.items || []),
  ]);
  if (!lines.length) return [];

  const rules = [];
  const pushRule = (id, label, description) => {
    const text = stripHtml(description);
    if (!text || rules.some((rule) => rule.description === text)) return;
    rules.push({
      id,
      rule_type: 'provider_fallback',
      label,
      description: text,
      refund_percent: /non-refundable|non refundable/i.test(text) ? 0 : null,
      from_days: null,
      to_days: null,
    });
  };

  const partialPaymentRule = lines.find((line) => /partial payments? are non-refundable/i.test(line));
  const refundProcessingRule = lines.find((line) => /refunds? will be processed within/i.test(line));
  const securityDepositRule = lines.find((line) => /security deposit refund/i.test(line));

  if (partialPaymentRule) {
    pushRule('provider-cancel-partial-payment', 'Partial payment', partialPaymentRule);
  }
  if (refundProcessingRule) {
    pushRule('provider-cancel-processing', 'Refund processing', refundProcessingRule);
  }
  if (securityDepositRule) {
    pushRule('provider-cancel-security', 'Security deposit', securityDepositRule);
  }

  if (!rules.length) {
    const refundSection = parsed.sections.find((section) => /refund|cancell/i.test(section.title || ''));
    if (refundSection?.items?.length) {
      refundSection.items.slice(0, 3).forEach((item, index) => {
        pushRule(`provider-cancel-fallback-${index + 1}`, refundSection.title || 'Cancellation', item);
      });
    }
  }

  return rules.slice(0, 3);
};

const normalizeProviderCancellationPolicy = (value) => {
  if (Array.isArray(value) && value.length) {
    return value
      .map((item, index) => {
        if (!item) return null;
        if (typeof item === 'string') {
          return {
            id: `provider-cancel-${index + 1}`,
            rule_type: 'provider',
            label: item,
            description: item,
            refund_percent: 0,
            from_days: null,
            to_days: null,
          };
        }
        const label =
          item?.title ||
          item?.name ||
          item?.label ||
          item?.policyType ||
          `Cancellation rule ${index + 1}`;
        const description = stripHtml(
          item?.description ||
            item?.content ||
            item?.text ||
            item?.policyDescription ||
            ''
        );
        const refundPercent = Number(
          item?.refundPercent ??
            item?.refund_percentage ??
            item?.refund ??
            0
        ) || 0;
        return {
          id: item?.id || `provider-cancel-${index + 1}`,
          rule_type: 'provider',
          label,
          description,
          refund_percent: refundPercent,
          from_days: item?.from_days ?? item?.fromDays ?? null,
          to_days: item?.to_days ?? item?.toDays ?? null,
        };
      })
      .filter(Boolean);
  }
  if (!value) return [];
  return [
    {
      id: 'provider-cancel-text',
      rule_type: 'provider',
      label: 'Cancellation terms',
      description: stripHtml(value),
      refund_percent: 0,
      from_days: null,
      to_days: null,
    },
  ];
};

const normalizeElivaasDetailPayload = (payload, propertyId, options = {}) => {
  const list = Array.isArray(payload?.list) ? payload.list : [];
  const fallbackProperty = options?.fallbackProperty || null;
  const fallbackQuotes = Array.isArray(fallbackProperty?.quotes) ? fallbackProperty.quotes : [];
  const fallbackSnapshot = fallbackProperty?.propertySnapshot || null;
  const fallbackRaw = fallbackSnapshot?.raw || fallbackProperty?.raw || null;
  const propertyRow =
    list.find((item) => String(item?.id || item?.propertyId || item?.property_id) === String(propertyId)) ||
    list[0] ||
    (fallbackRaw
      ? {
          ...fallbackRaw,
          id: propertyId,
          provider_property_id: String(propertyId || '').trim(),
          propertyName: fallbackProperty?.propertyName || fallbackSnapshot?.title || fallbackRaw?.name || '',
          quotes: fallbackQuotes,
          raw: fallbackRaw,
        }
      : null) ||
    null;
  const quoteList =
    Array.isArray(propertyRow?.quotes) && propertyRow.quotes.length ? propertyRow.quotes : fallbackQuotes;
  const firstQuote = Array.isArray(quoteList) ? quoteList[0] : null;
  const selectedQuote = pickProviderQuote(quoteList, options) || firstQuote;
  const snapshot =
    selectedQuote?.homevilasQuote?.property_snapshot ||
    firstQuote?.homevilasQuote?.property_snapshot ||
    fallbackSnapshot ||
    null;
  const raw = snapshot?.raw || propertyRow?.raw || propertyRow || fallbackRaw || null;
  if (!snapshot && !raw) {
    throw new Error('Provider property not found.');
  }

  const metrics = Array.isArray(raw?.metrics) ? raw.metrics : [];
  const houseRulesSection = findProviderSection(raw, 'HOUSE_RULES');
  const aboutSection = findProviderSection(raw, 'VILLA_SNAPSHOT');
  const mealsSection = findProviderSection(raw, 'MEALS');
  const nearbySection = findProviderSection(raw, 'NEAREST_PLACES');
  const houseRulesParsed = parseProviderHtmlContent(houseRulesSection?.content);
  const aboutParsed = parseProviderHtmlContent(
    aboutSection?.content || raw?.description || snapshot?.description || ''
  );
  const mealsParsed = parseProviderHtmlContent(mealsSection?.content);
  const faqItems = parseProviderFaqs(raw?.faqs);
  const houseRuleLines = uniqueProviderItems([
    ...houseRulesParsed.list,
    ...houseRulesParsed.paragraphs,
  ]);
  const topAmenities = toAmenityItems(raw?.topAmenities || raw?.top_amenities || snapshot?.topAmenities || []).map((item) => ({
    ...item,
    is_top_feature: true,
  }));
  const fallbackAmenities = buildProviderFallbackAmenities(raw, propertyRow, snapshot);
  const allAmenities = mergeAmenityItems(
    topAmenities,
    toAmenityItems(snapshot?.amenity_items || snapshot?.amenities || []),
    toAmenityItems(propertyRow?.amenity_items || propertyRow?.amenities || []),
    toAmenityItems(raw?.amenity_items || raw?.amenities || []),
    toAmenityItems(raw?.top_amenities || raw?.amenityList || raw?.amenity_list || []),
    toAmenityItems(raw?.amenitiesList || raw?.amenities_list || raw?.propertyAmenities || raw?.property_amenities || []),
    toAmenityItems(raw?.facilities || raw?.facilityList || raw?.facility_list || []),
    toAmenityItems(raw?.features || raw?.inclusions || raw?.highlights || []),
    fallbackAmenities
  );
  const description =
    aboutParsed.text ||
    stripHtml(raw?.description || snapshot?.description || snapshot?.raw?.description || '');
  const checkInTime =
    raw?.checkInTime ||
    raw?.check_in_time ||
    extractProviderTime(houseRulesSection?.content, 'Check-in') ||
    '';
  const checkOutTime =
    raw?.checkOutTime ||
    raw?.check_out_time ||
    extractProviderTime(houseRulesSection?.content, 'Check-out') ||
    '';
  const bedrooms =
    toNumber(snapshot?.bedrooms || raw?.bedrooms || raw?.bedroomCount || raw?.numberOfBedrooms) ||
    extractProviderMetricNumber(metrics, 'bedroom') ||
    extractProviderMetricNumber(metrics, 'bed');
  const bathrooms =
    toNumber(snapshot?.bathrooms || raw?.bathrooms || raw?.bathroomCount || raw?.numberOfBathrooms) ||
    extractProviderMetricNumber(metrics, 'bathroom');
  const guests =
    toNumber(snapshot?.max_guests || raw?.maxOccupancy || raw?.maxAdults) ||
    extractProviderMetricNumber(metrics, 'guest');
  const spaces = (Array.isArray(raw?.spaces) ? raw.spaces : [])
    .map((space, index) => {
      const title = String(space?.title || '').trim();
      const imageUrl = space?.image_url || space?.image || space?.url || '';
      if (!title && !imageUrl) return null;
      return {
        id: space?.id || `provider-space-${index + 1}`,
        title: title || `Space ${index + 1}`,
        subtitle: String(space?.subtitle || space?.description || '').trim(),
        image_url: imageUrl,
        sort_order: index + 1,
      };
    })
    .filter(Boolean);
  const nearbyPoints = parseNearbyPointsFromSection(nearbySection);
  const cancellationPolicySource =
    selectedQuote?.cancellationPolicies ||
    selectedQuote?.cancellationPolicy ||
    selectedQuote?.homevilasQuote?.provider_quote?.cancellationPolicies ||
    selectedQuote?.homevilasQuote?.provider_quote?.cancellationPolicy ||
    firstQuote?.cancellationPolicies ||
    firstQuote?.cancellationPolicy ||
    firstQuote?.homevilasQuote?.provider_quote?.cancellationPolicies ||
    firstQuote?.homevilasQuote?.provider_quote?.cancellationPolicy ||
    '';
  const normalizedProviderCancellationPolicy = normalizeProviderCancellationPolicy(cancellationPolicySource);
  const cancellationPolicy =
    normalizedProviderCancellationPolicy.length > 0
      ? normalizedProviderCancellationPolicy
      : buildProviderCancellationFallback(houseRulesSection);
  const mealPlans = buildProviderMealPlans(
    quoteList,
    selectedQuote,
    mealsParsed.text || 'Provider meal/package plan'
  );
  const offers = buildProviderOfferCards(selectedQuote?.homevilasQuote || firstQuote?.homevilasQuote, quoteList);
  const reviewSources = buildProviderReviewSources(propertyRow);
  const providerReviews = normalizeProviderReviews(snapshot, propertyRow, raw);
  const providerReviewMetrics =
    propertyRow?.review_metrics ||
    snapshot?.review_metrics ||
    raw?.review_metrics ||
    null;
  const featureBadges = buildProviderFeatureBadges(propertyRow, snapshot, houseRulesSection?.content || '');
  const extraSections = buildProviderExtraSections(raw, [
    'VILLA_SNAPSHOT',
    'HOUSE_RULES',
    'MEALS',
    'NEAREST_PLACES',
  ]);
  const providerAddons = buildProviderFallbackAddons({
    raw,
    aboutText: `${description} ${aboutSection?.content || ''}`,
    houseRulesText: houseRulesSection?.content || '',
    mealsSection,
    spaces,
    images: snapshot?.images || raw?.images || [],
  });
  const providerImages = toImageObjects(
    snapshot?.images ||
      raw?.images ||
      raw?.gallery ||
      raw?.galleryImages ||
      raw?.propertyImages ||
      raw?.property_images ||
      raw?.photos ||
      raw?.media ||
      raw?.medias ||
      raw?.assets ||
      raw?.rooms ||
      raw?.roomTypes ||
      raw?.spaces ||
      snapshot?.featured_image ||
      raw?.featured_image ||
      raw?.cover_image ||
      raw?.coverImage ||
      raw?.imageUrl ||
      raw?.image ||
      raw?.defaultMedia ||
      [],
    snapshot?.title ||
      snapshot?.name ||
      propertyRow?.propertyName ||
      raw?.name ||
      `Elivaas Property ${propertyId}`
  );

  const property = {
    id: `provider:elivaas:${propertyId}`,
    slug: String(propertyId || '').trim() || `elivaas-${Date.now()}`,
    provider_code: 'elivaas',
    provider_property_id: String(propertyId || '').trim(),
    title:
      snapshot?.title ||
      snapshot?.name ||
      propertyRow?.propertyName ||
      raw?.name ||
      `Elivaas Property ${propertyId}`,
    name:
      snapshot?.title ||
      snapshot?.name ||
      propertyRow?.propertyName ||
      raw?.name ||
      `Elivaas Property ${propertyId}`,
    description,
    long_description: description,
    city: snapshot?.city || raw?.city || '',
    state: snapshot?.state || raw?.state || '',
    country: snapshot?.country || raw?.country || 'India',
    area: snapshot?.area || raw?.area || raw?.state || '',
    location_label:
      snapshot?.location_label ||
      [snapshot?.city || raw?.city, snapshot?.area || raw?.state, snapshot?.country || raw?.country || 'India']
        .filter(Boolean)
        .join(', '),
    currency: snapshot?.currency || propertyRow?.currency || raw?.currency || 'INR',
    images: providerImages,
    room_types: [],
    max_guests: guests || 0,
    bedrooms: bedrooms || 0,
    bathrooms: bathrooms || 0,
    avg_rating: readProviderRating(snapshot, propertyRow, raw),
    review_count: readProviderReviewCount(snapshot, propertyRow, raw),
    review_metrics: providerReviewMetrics,
    latitude: snapshot?.latitude ?? raw?.latitude ?? raw?.lat ?? null,
    longitude: snapshot?.longitude ?? raw?.longitude ?? raw?.lng ?? null,
    brochure_url: raw?.brandedBrochure || raw?.nonBrandedBrochure || '',
    house_rules_sections: houseRuleLines.length
      ? [{ title: 'House Rules', items: houseRuleLines }]
      : [],
    security_deposit_policy: '',
    check_in_time: checkInTime,
    check_out_time: checkOutTime,
    min_nights: extractProviderMinNights(houseRulesSection?.content),
    quote_id: String(selectedQuote?.quoteId || ''),
    rate_plan_code: String(selectedQuote?.ratePlanCode || ''),
    provider_base_price:
      Number(
        propertyRow?.provider_base_price ??
          propertyRow?.base_price_per_night ??
          propertyRow?.priceAmount ??
          selectedQuote?.homevilasQuote?.nightly_provider_rate ??
          0
      ) || 0,
    margin_type:
      selectedQuote?.homevilasQuote?.margin_type ||
      firstQuote?.homevilasQuote?.margin_type ||
      propertyRow?.margin_type ||
      'percent',
    margin_value:
      Number(
        selectedQuote?.homevilasQuote?.margin_value ??
          firstQuote?.homevilasQuote?.margin_value ??
          propertyRow?.margin_value ??
          propertyRow?.platform_margin_percent ??
          0
      ) || 0,
    display_sell_price:
      Number(
        propertyRow?.display_sell_price ??
          selectedQuote?.homevilasQuote?.nightly_sell_rate ??
          firstQuote?.homevilasQuote?.nightly_sell_rate ??
          propertyRow?.sellPricePerNight ??
          propertyRow?.sell_price_per_night ??
          0
      ) || 0,
    price_source: propertyRow?.price_source || 'api',
    final_per_night_display_price:
      Number(
        propertyRow?.display_sell_price ??
          selectedQuote?.homevilasQuote?.nightly_sell_rate ??
          firstQuote?.homevilasQuote?.nightly_sell_rate ??
          selectedQuote?.homevilasQuote?.final_per_night_display_price ??
          firstQuote?.homevilasQuote?.final_per_night_display_price ??
          propertyRow?.sellPricePerNight ??
          propertyRow?.sell_price_per_night ??
          propertyRow?.price_per_night ??
          propertyRow?.base_price_per_night ??
          snapshot?.price_per_night ??
          propertyRow?.priceAmount ??
          0
      ) || 0,
  };
  const aboutHomeTabs = [
    {
      id: 'checkin',
      label: 'Check-in',
      content: {
        text: [
          property.check_in_time ? `Check-in from ${property.check_in_time}.` : '',
          property.check_out_time ? `Check-out by ${property.check_out_time}.` : '',
          property.min_nights ? `Minimum stay: ${property.min_nights} night${property.min_nights > 1 ? 's' : ''}.` : '',
        ]
          .filter(Boolean)
          .join(' '),
      },
    },
  ];
  if (description || aboutParsed.sections.length) {
    aboutHomeTabs.push({
      id: 'about-stay',
      label: 'About Stay',
      content: {
        text: description,
        sections: aboutParsed.sections,
      },
    });
  }
  if (houseRuleLines.length || houseRulesParsed.sections.length) {
    aboutHomeTabs.push({
      id: 'house-rules',
      label: 'House Rules',
      content: {
        list: houseRuleLines,
        sections: houseRulesParsed.sections,
      },
    });
  }
  if (mealsParsed.text || mealsParsed.sections.length || mealsParsed.list.length) {
    aboutHomeTabs.push({
      id: 'meals',
      label: 'Meals',
      content: {
        text: mealsParsed.text,
        list: mealsParsed.list,
        sections: mealsParsed.sections,
      },
    });
  }
  if (faqItems.length) {
    aboutHomeTabs.push({
      id: 'faq',
      label: 'FAQs',
      content: {
        faqs: faqItems,
      },
    });
  }

  return normalizePropertyDetailPayload(
    {
      property,
      images: property.images,
      amenities: {
        items: allAmenities,
        top_features: topAmenities,
      },
      spaces,
      offers,
      addons: providerAddons,
      reels: [],
      nearbyPoints,
      cancellationPolicy,
      featureBadges,
      reviewSources,
      reviews: {
        averageRating: providerReviewMetrics?.averageRating ?? property.avg_rating ?? null,
        reviewCount: providerReviewMetrics?.reviewCount ?? property.review_count ?? null,
        metrics: Array.isArray(providerReviewMetrics?.metrics) ? providerReviewMetrics.metrics : [],
        source: providerReviewMetrics?.source || 'elivaas',
        average_rating: (providerReviewMetrics?.averageRating ?? property.avg_rating) || 0,
        total: (providerReviewMetrics?.reviewCount ?? property.review_count) || providerReviews.length || 0,
        summary: '',
        items: providerReviews,
      },
      mealPlans,
      pricingContext: {
        availableCoupons: selectedQuote?.homevilasQuote?.available_coupons || firstQuote?.homevilasQuote?.available_coupons || [],
        eligibleCardOffers: selectedQuote?.homevilasQuote?.eligible_card_offers || firstQuote?.homevilasQuote?.eligible_card_offers || [],
        providerOfferCards: offers,
      },
      aboutHomeTabs,
      extraSections,
      displayFlags: {
        show_brochure_button: Boolean(property.brochure_url),
        show_feature_badges: featureBadges.length > 0,
        show_offer_cards: offers.length > 0,
        show_addons: providerAddons.length > 0,
        show_spaces: spaces.length > 0,
        show_map_section: Boolean(property.latitude && property.longitude),
        show_nearby_places: nearbyPoints.length > 0,
        show_story_reels: false,
        show_cancellation_policy: cancellationPolicy.length > 0,
        show_review_sources: reviewSources.length > 0,
        show_contact_block: Boolean(raw?.phoneNumber || raw?.whatsappNumber),
        show_coupon_box: true,
        show_price_breakup: true,
        show_savings_banner: true,
        show_meals_selector: mealPlans.length > 0,
        contact_phone: raw?.phoneNumber || '',
        contact_whatsapp: raw?.whatsappNumber || '',
      },
    },
    propertyId
  );
};

const normalizePropertyDetailPayload = (payload, slug) => {
  const root =
    payload && typeof payload === 'object'
      ? payload
      : payload?.data && typeof payload.data === 'object'
        ? payload.data
        : null;
  const propertySource =
    root?.property && typeof root.property === 'object'
      ? root.property
      : root?.data && typeof root.data === 'object'
        ? root.data
        : root;

  if (!propertySource || typeof propertySource !== 'object') return payload;

  const city = propertySource.city || propertySource.location?.city || '';
  const state = propertySource.state || propertySource.location?.state || '';
  const country = propertySource.country || propertySource.location?.country || '';
  const title = propertySource.title || propertySource.name || 'Homevilas Stay';
  const description = stripHtml(propertySource.long_description || propertySource.description || '');
  const images = toImageObjects(root?.images ?? propertySource.images, title);
  const amenityItems = toAmenityItems(root?.amenities?.items ?? root?.amenities ?? propertySource.amenities);
  const amenityTopFeatures = mergeAmenityItems(
    toAmenityItems(root?.amenities?.top_features ?? root?.amenities?.topFeatures),
    amenityItems.filter((item) => item.is_top_feature)
  );
  const roomTypes = normalizeRoomTypes(root?.room_types ?? propertySource.room_types, propertySource);

  const property = {
    ...propertySource,
    id: propertySource.id || propertySource._id || slug,
    slug: propertySource.slug || slug,
    title,
    name: title,
    description,
    long_description: description,
    city,
    state,
    country,
    area: propertySource.area || state || '',
    location_label:
      propertySource.location_label ||
      [city, state, country].filter(Boolean).join(', '),
    currency: propertySource.currency || 'INR',
    images,
    room_types: roomTypes,
    max_guests: toNumber(propertySource.max_guests ?? propertySource.maxGuests ?? propertySource.guests),
    bedrooms: toNumber(propertySource.bedrooms ?? propertySource.rooms),
    bathrooms: toNumber(propertySource.bathrooms),
    avg_rating: Number(propertySource.avg_rating ?? propertySource.rating ?? root?.reviewSummary?.avg ?? 0) || 0,
    review_count: toNumber(propertySource.review_count ?? propertySource.reviewCount ?? propertySource.reviewsCount ?? root?.reviewSummary?.count),
    review_metrics: propertySource.review_metrics || root?.reviews || null,
    latitude: propertySource.latitude ?? propertySource.lat ?? null,
    longitude: propertySource.longitude ?? propertySource.lng ?? null,
    brochure_url:
      propertySource.brochure_url ||
      propertySource.brochure ||
      propertySource.brochure_file ||
      propertySource.brochure_link ||
      '',
    house_rules_sections: propertySource.house_rules_sections || [],
    security_deposit_policy: propertySource.security_deposit_policy || '',
    check_in_time: propertySource.check_in_time || '',
    check_out_time: propertySource.check_out_time || '',
    min_nights: toNumber(propertySource.min_nights ?? propertySource.minNights),
  };

  const offers = Array.isArray(root?.offers) ? root.offers : Array.isArray(propertySource.offers) ? propertySource.offers : [];
  const addons = normalizeAddonItems(
    Array.isArray(root?.addons) ? root.addons : Array.isArray(propertySource.addons) ? propertySource.addons : []
  );
  const reels = Array.isArray(root?.reels) ? root.reels : Array.isArray(propertySource.reels) ? propertySource.reels : [];
  const spaces = Array.isArray(root?.spaces) ? root.spaces : Array.isArray(propertySource.spaces) ? propertySource.spaces : [];
  const nearbyPoints = Array.isArray(root?.nearbyPoints) ? root.nearbyPoints : [];
  const cancellationPolicy = Array.isArray(root?.cancellationPolicy)
    ? root.cancellationPolicy
    : buildCancellationPolicy(propertySource);
  const featureBadges = Array.isArray(root?.featureBadges)
    ? root.featureBadges
    : buildFeatureBadges(propertySource);
  const reviewSources = Array.isArray(root?.reviewSources) ? root.reviewSources : [];
  const reviewsSource = root?.reviews && typeof root.reviews === 'object' ? root.reviews : null;
  const reviews = {
    averageRating:
      Number(reviewsSource?.averageRating ?? property.review_metrics?.averageRating ?? property.avg_rating ?? root?.reviewSummary?.avg ?? 0) || null,
    reviewCount:
      toNumber(reviewsSource?.reviewCount ?? property.review_metrics?.reviewCount ?? property.review_count ?? root?.reviewSummary?.count),
    metrics: Array.isArray(reviewsSource?.metrics)
      ? reviewsSource.metrics
      : Array.isArray(property.review_metrics?.metrics)
        ? property.review_metrics.metrics
        : [],
    source: reviewsSource?.source || property.review_metrics?.source || '',
    average_rating:
      Number(reviewsSource?.average_rating ?? reviewsSource?.avg ?? property.avg_rating ?? root?.reviewSummary?.avg ?? 0) || 0,
    total:
      toNumber(reviewsSource?.total ?? reviewsSource?.count ?? property.review_count ?? root?.reviewSummary?.count),
    summary:
      reviewsSource?.summary ||
      property.review_summary ||
      root?.reviewSummary?.summary ||
      '',
    items: Array.isArray(reviewsSource?.items) ? reviewsSource.items : [],
  };
  const availableCoupons = Array.isArray(root?.pricingContext?.availableCoupons)
    ? root.pricingContext.availableCoupons
    : [];
  const aboutHomeTabs = Array.isArray(root?.aboutHomeTabs)
    ? root.aboutHomeTabs
    : buildAboutHomeTabs(property, cancellationPolicy);
  const displayFlags = {
    show_brochure_button: Boolean(property.brochure_url),
    show_feature_badges: featureBadges.length > 0,
    show_offer_cards: offers.length > 0,
    show_addons: addons.length > 0,
    show_spaces: spaces.length > 0,
    show_map_section: Boolean(property.latitude && property.longitude),
    show_nearby_places: nearbyPoints.length > 0,
    show_story_reels: reels.length > 0,
    show_cancellation_policy: cancellationPolicy.length > 0,
    show_review_sources: reviewSources.length > 0,
    show_contact_block: false,
    show_coupon_box: true,
    show_price_breakup: true,
    show_savings_banner: true,
    ...(root?.displayFlags && typeof root.displayFlags === 'object' ? root.displayFlags : {}),
  };

  return {
    ...root,
    property,
    offers,
    addons,
    reels,
    spaces,
    nearbyPoints,
    cancellationPolicy,
    featureBadges,
    reviewSources,
    reviews,
    amenities: {
      items: amenityItems,
      top_features: amenityTopFeatures.length ? amenityTopFeatures : amenityItems.slice(0, 4),
    },
    mealPlans: Array.isArray(root?.mealPlans) ? root.mealPlans : Array.isArray(propertySource.mealPlans) ? propertySource.mealPlans : [],
    pricingContext: {
      availableCoupons,
      ...(root?.pricingContext && typeof root.pricingContext === 'object' ? root.pricingContext : {}),
    },
    aboutHomeTabs,
    displayFlags,
  };
};

const fetchElivaasPropertyFallback = async (propertyId, options = {}) => {
  const payload = await apiGet(
    `/api/providers/elivaas/properties/${encodeURIComponent(propertyId)}${qs({
      checkIn: options?.checkIn,
      checkOut: options?.checkOut,
      adults: options?.adults,
      children: options?.children,
      destination: options?.destination,
      city: options?.city,
    })}`
  );
  return payload?.data || payload?.item || payload || null;
};

const isUsableElivaasFallbackRow = (row = {}, propertyId = '') => {
  if (!row || typeof row !== 'object') return false;
  if (row.message && Object.keys(row).length <= 2) return false;
  const rowId = String(row.provider_property_id || row.propertyId || row.property_id || row.id || '').trim();
  if (rowId && rowId === String(propertyId || '').trim()) return true;
  return Boolean(
    row.title ||
      row.name ||
      row.propertyName ||
      row.featured_image ||
      row.coverImage ||
      row.image_url ||
      (Array.isArray(row.images) && row.images.length)
  );
};

const fetchElivaasPropertyFromPublicListing = async (propertyId, options = {}) => {
  const search = String(options?.propertyName || propertyId || '').trim();
  if (!search) return null;
  const buildPath = (params) =>
    `/api/public/properties${qs({
      search,
      source: 'api',
      limit: 20,
      ...params,
    })}`;
  const readRows = (payload) =>
    Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
  const pickMatch = (rows) =>
    rows.find((item) => String(item?.provider_property_id || item?.slug || item?.id || '') === String(propertyId)) ||
    rows[0] ||
    null;

  const datedPayload = await apiGet(
    buildPath({
      checkIn: options?.checkIn,
      checkOut: options?.checkOut,
      adults: options?.adults,
      children: options?.children,
    })
  );
  const datedMatch = pickMatch(readRows(datedPayload));
  if (datedMatch || (!options?.checkIn && !options?.checkOut)) return datedMatch;

  const catalogPayload = await apiGet(
    buildPath({
      adults: options?.adults,
      children: options?.children,
    })
  );
  return pickMatch(readRows(catalogPayload));
};

const normalizeElivaasCheckoutStayData = (propertyId, payload = {}) => {
  const source = payload?.data || payload?.item || payload || {};
  const title = source.title || source.name || `Elivaas Property ${propertyId}`;
  const firstImage = Array.isArray(source.images) ? source.images[0] : null;
  return {
    ...source,
    id: `provider:elivaas:${propertyId}`,
    slug: String(source.slug || propertyId || '').trim(),
    title,
    name: title,
    provider_code: 'elivaas',
    provider_property_id: String(source.provider_property_id || propertyId || '').trim(),
    city: source.city || '',
    area: source.area || source.state || '',
    currency: source.currency || 'INR',
    coverImage:
      source.coverImage ||
      source.featured_image ||
      source.featuredImage ||
      source.image_url ||
      firstImage?.image_url ||
      firstImage?.url ||
      firstImage ||
      '',
  };
};

export const getPropertyDetailFull = async (slug, options = {}) => {
  const providerCode = String(options?.providerCode || '').trim().toLowerCase();
  if (providerCode === 'elivaas') {
    const cachedFallback = getCachedElivaasProperty(slug);
    const providerFallbackRow = await fetchElivaasPropertyFallback(slug, options).catch(() => null);
    const fallbackRow = isUsableElivaasFallbackRow(providerFallbackRow, slug) ? providerFallbackRow : null;
    const listingFallback =
      fallbackRow || cachedFallback
        ? null
        : await fetchElivaasPropertyFromPublicListing(slug, options).catch(() => null);
    if (fallbackRow || listingFallback || cachedFallback) {
      return normalizeElivaasDetailPayload(
        fallbackRow || listingFallback ? { list: [fallbackRow || listingFallback] } : { list: [] },
        slug,
        {
          quoteId: options?.quoteId,
          ratePlanCode: options?.ratePlanCode,
          fallbackProperty: cachedFallback,
        }
      );
    }
    try {
      const payload = await searchRates({
        propertyId: slug,
        pageSize: 1,
        checkinDate: options?.checkIn,
        checkoutDate: options?.checkOut,
        adults: options?.adults,
        children: options?.children,
      });
      cacheElivaasRates(payload);
      return normalizeElivaasDetailPayload(payload, slug, {
        quoteId: options?.quoteId,
        ratePlanCode: options?.ratePlanCode,
      });
    } catch (err) {
      const providerFallbackRow = await fetchElivaasPropertyFallback(slug, options).catch(() => null);
      const fallbackRow = isUsableElivaasFallbackRow(providerFallbackRow, slug) ? providerFallbackRow : null;
      const listingFallback = fallbackRow
        ? null
        : await fetchElivaasPropertyFromPublicListing(slug, options).catch(() => null);
      if (fallbackRow || listingFallback) {
        return normalizeElivaasDetailPayload(
          { list: [fallbackRow || listingFallback] },
          slug,
          {
            quoteId: options?.quoteId,
            ratePlanCode: options?.ratePlanCode,
          }
        );
      }
      throw err;
    }
  }
  const encodedSlug = encodeURIComponent(slug);
  const errors = [];
  const tryDetailEndpoint = async (path) => {
    try {
      const payload = await apiGet(path);
      return normalizePropertyDetailPayload(payload, slug);
    } catch (err) {
      errors.push(err);
      return null;
    }
  };

  const publicPayload = await tryDetailEndpoint(`/api/public/properties/${encodedSlug}`);
  if (publicPayload) return publicPayload;

  const fullPayload = await tryDetailEndpoint(`/api/properties/${encodedSlug}/detail-full`);
  if (fullPayload) return fullPayload;

  const unauthorized = errors.find((err) => err?.status === 401);
  if (unauthorized) {
    const error = new Error('Unable to load property. Please try again.');
    error.status = 500;
    throw error;
  }

  const notFound = errors.find((err) => err?.status === 404);
  if (notFound) {
    const error = new Error('Property not found');
    error.status = 404;
    throw error;
  }
  const error = new Error('Unable to load property. Please try again.');
  error.status = errors.find((err) => err?.status)?.status || 500;
  throw error;
};

export const getCachedElivaasDetail = (propertyId, options = {}) => {
  if (typeof window === 'undefined') return null;
  const cached = getCachedElivaasProperty(propertyId);
  const fallbackTitle = String(options?.propertyName || '').trim();
  const fallbackImage = String(options?.image || '').trim();
  if (!cached && !fallbackTitle && !fallbackImage) return null;
  const propertySnapshot = cached?.propertySnapshot || {
    title: fallbackTitle || `Homevilas Property ${propertyId}`,
    name: fallbackTitle || `Homevilas Property ${propertyId}`,
    city: options?.city || '',
    area: options?.area || '',
    country: 'India',
    images: fallbackImage ? [fallbackImage] : [],
    raw: {
      id: propertyId,
      name: fallbackTitle || `Homevilas Property ${propertyId}`,
      city: options?.city || '',
      area: options?.area || '',
      country: 'India',
      images: fallbackImage ? [fallbackImage] : [],
    },
  };
  return normalizeElivaasDetailPayload(
    { list: [] },
    propertyId,
    {
      quoteId: options?.quoteId,
      ratePlanCode: options?.ratePlanCode,
      fallbackProperty: {
        id: propertyId,
        propertyName: cached?.propertyName || fallbackTitle || propertySnapshot.title,
        propertySnapshot,
        quotes: cached?.quotes || [],
        raw: propertySnapshot.raw || propertySnapshot,
      },
    }
  );
};
export const getPropertyPricingPreview = (id, params = {}) =>
  apiGet(`/api/properties/${encodeURIComponent(id)}/pricing-preview${qs(params)}`);
export const getCheckoutStayData = async (stayId) => {
  const rawStayId = String(stayId || '').trim();
  const providerMatch = rawStayId.match(/^provider:elivaas:(.+)$/i);
  if (providerMatch) {
    const providerPropertyId = String(providerMatch[1] || '').trim();
    const payload = await fetchElivaasPropertyFallback(providerPropertyId);
    return normalizeElivaasCheckoutStayData(providerPropertyId, payload);
  }
  const payload = await apiGet(`/api/public/properties/${encodeURIComponent(rawStayId)}`);
  return {
    ...payload,
    id: payload.id || payload.property_id || rawStayId,
    slug: payload.slug || payload.id || rawStayId,
    coverImage: payload.coverImage || payload.featured_image,
  };
};
export const previewBookingPrice = (payload = {}) =>
  apiPost('/api/public/pricing/quote', {
    property_id: payload.property_id || payload.propertyId || payload.stayId,
    check_in: payload.check_in || payload.checkIn,
    check_out: payload.check_out || payload.checkOut,
    guests_count: payload.guests_count || payload.guests,
    adults: payload.adults,
    children: payload.children,
    room_type_id: payload.room_type_id || payload.roomTypeId || undefined,
    room_count: payload.room_count || payload.roomCount || undefined,
    meal_plan_id: payload.meal_plan_id || payload.mealPlanId || undefined,
    addon_ids: payload.addon_ids || payload.addonIds || undefined,
    promo_code: payload.promo_code || payload.promoCode || payload.couponCode || undefined,
    couponId: payload.couponId || payload.coupon_id || undefined,
    cardOfferId: payload.cardOfferId || payload.card_offer_id || undefined,
    quote_id: payload.quote_id || payload.quoteId || undefined,
    rate_plan_code:
      payload.rate_plan_code ||
      payload.ratePlanCode ||
      payload.meal_plan_id ||
      payload.mealPlanId ||
      undefined,
  });
export const validatePropertyCoupon = (payload) => apiPost('/api/coupons/validate', payload);
export const getPropertyOffers = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/offers`);
export const getPropertyAddons = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/addons`);
export const getPropertyReels = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/reels`);
export const getPropertyNearbyPoints = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/nearby-points`);
export const getPropertyCancellationPolicy = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/cancellation-policy`);
export const getPropertyReviewSources = (id) => apiGet(`/api/properties/${encodeURIComponent(id)}/review-sources`);

export const getAdminPropertyPricingDeals = (id) => apiGet(`/api/admin/properties/${encodeURIComponent(id)}/pricing-deals`);
export const saveAdminPropertyPricingDeals = (id, payload) =>
  apiPut(`/api/admin/properties/${encodeURIComponent(id)}/pricing-deals`, payload);
export const saveAdminPropertyDisplayFlags = (id, payload) =>
  apiPut(`/api/admin/properties/${encodeURIComponent(id)}/display-flags`, payload);
